import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLineUp } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Sprint 66 — Weekly backtest: every Smart build is snapshotted; Tuesday's
// job pulls actual DK points (nflverse) and grades it vs the field and a
// mean-projection baseline. This panel shows the running scorecard.
const pct = (v) => (v == null ? "—" : `${Number(v) > 0 ? "+" : ""}${Number(v).toFixed(1)}%`);

export default function BacktestPanel() {
  const [data, setData] = useState(null);
  const [snaps, setSnaps] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([http.get("/backtest/results"), http.get("/backtest/snapshots")]);
      setData(r.data);
      setSnaps(s.data);
    } catch (e) { /* silent — panel is informational */ }
  };
  useEffect(() => { load(); window.addEventListener("wp:lineups-refresh", load); return () => window.removeEventListener("wp:lineups-refresh", load); }, []);

  const gradeNow = async () => {
    setBusy(true);
    try {
      const r = await http.post("/backtest/grade", { force: false });
      const d = r.data || {};
      toast[d.graded ? "success" : "info"](`Graded ${d.graded} · skipped ${d.skipped} (games not final or stats not published yet)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grading failed");
    } finally { setBusy(false); }
  };

  const s = data?.summary || {};
  const pending = (snaps?.snapshots || []).filter((x) => !x.graded).length;
  return (
    <div className="bg-[#0a0a0a] border border-zinc-800" data-testid="backtest-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="backtest-toggle">
        <div>
          <div className="overline flex items-center gap-2"><ChartLineUp size={12} className="text-[#FFCC00]" /> Weekly Backtest</div>
          <div className="mono text-[10px] text-zinc-400 mt-0.5" data-testid="backtest-summary">
            {s.builds ? `${s.weeks} wk · ${s.builds} builds · WarPaint ROI ${pct(s.warpaint_roi_pct)} vs baseline ${pct(s.baseline_roi_pct)} · best finish top ${Number(s.best_finish_pct).toFixed(2)}% · cash ${s.cash_rate_pct}%`
              : `${pending} build${pending === 1 ? "" : "s"} snapshotted · grades itself Tuesday 10:00 CT once stats publish`}
          </div>
        </div>
        <span className="text-zinc-500 text-xs">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-zinc-900 pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="mono text-[10px] text-zinc-500">{pending} pending · {data?.results?.length || 0} graded</span>
            <button type="button" onClick={gradeNow} disabled={busy} data-testid="backtest-grade-now"
              className="px-3 py-1 text-[10px] mono uppercase tracking-widest border border-[#FFCC00]/60 text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black disabled:opacity-50 transition-colors font-bold">
              {busy ? "Grading…" : "Grade now"}
            </button>
          </div>
          {(data?.results || []).slice(0, 8).map((r, i) => {
            const wp = (r.results || []).find((x) => x.label === "WarPaint") || {};
            const bl = (r.results || []).find((x) => String(x.label).startsWith("Mean")) || {};
            return (
              <div key={i} className="mono text-[10px] border border-zinc-900 px-2 py-1.5" data-testid={`backtest-row-${i}`}>
                <div className="flex justify-between text-zinc-300">
                  <span>{r.season} W{r.week} · {r.contest_type} · {r.preset}{r.blend ? ` (${r.blend})` : ""}</span>
                  <span className={wp.roi_pct >= 0 ? "text-[#39FF14]" : "text-zinc-400"}>ROI {pct(wp.roi_pct)}</span>
                </div>
                <div className="flex justify-between text-zinc-500 mt-0.5">
                  <span>best top {Number(wp.best_rank_pct).toFixed(2)}% · cash {wp.cashes}/{wp.lineups} · top-1% {wp.top1pct}</span>
                  <span>baseline {pct(bl.roi_pct)} · best {bl.best_rank_pct != null ? Number(bl.best_rank_pct).toFixed(2) + "%" : "—"}</span>
                </div>
                {r.rostered_coverage_pct != null && r.rostered_coverage_pct < 90 && (
                  <div className="text-[#FF3B30] mt-0.5">only {r.rostered_coverage_pct}% of rostered players had actuals</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
